import { createEvent, createStore } from 'effector';
import { effectorStartEvent } from '@nx-frontend/shared/store/client-side';
import {
  PortalAIAssistantSettings,
  PortalClientSettings,
  PortalRequestAccessPageProps,
  PortalSettings,
  PortalSettingsAccess,
} from '@nx-frontend/shared/types';

export interface SettingsState {
  name: string;
  description: string;
  language: string;
  domain: string;
  portalId: string;
  searchEnabled: boolean;
  greetingEnabled: boolean;
  greetingMessage: string;
  headerBlocksEnabled: boolean;
  access: PortalSettingsAccess;
  aiAssistantSettings: PortalAIAssistantSettings;
  searchAnalyticsEnabled: boolean;
  portalAnalyticsEnabled: boolean;
  searchIndexEnabled: boolean;
  customCode: string;
  customScript: string;
  customStyle: string;
  disableBranding?: boolean;
  clientSettings: PortalClientSettings;
  requestAccessPage: {
    title: string | null;
    description: string | null;
  };
}

export const initialClientSettings: PortalClientSettings = {
  createTasks: true,
  editTasks: true,
  commentTasks: true,
  changeTasksStatus: true,
  showComments: true,
};

export const initialRequestAccessPage: PortalRequestAccessPageProps = {
  title: null,
  description: null,
};

export const initialAIAssistantSettings = { availability: { loggedIn: false, anyone: false } };

export const initialSettingsState: SettingsState = {
  name: '',
  description: '',
  language: 'en-US',
  domain: '',
  portalId: '',
  searchEnabled: true,
  greetingEnabled: true,
  greetingMessage: 'Welcome to your Portal',
  headerBlocksEnabled: false,
  searchAnalyticsEnabled: false,
  portalAnalyticsEnabled: false,
  searchIndexEnabled: false,
  disableBranding: true,
  customCode: '',
  customScript: '',
  customStyle: '',
  access: {
    console: { owner: true, managers: true, members: true, guests: false },
    portal: {
      owner: true,
      managers: true,
      members: true,
      guests: true,
      clients: true,
      anyone: true,
      skipClientValidation: false,
      requireClientEmail: false,
    },
  },
  clientSettings: initialClientSettings,
  requestAccessPage: initialRequestAccessPage,
  aiAssistantSettings: initialAIAssistantSettings,
};

export const updateSearchAnalyticsSettingsEvent = createEvent<Pick<PortalSettings, 'searchAnalyticsEnabled'>>();
export const updatePortalAnalyticsSettingsEvent = createEvent<Pick<PortalSettings, 'portalAnalyticsEnabled'>>();
export const updateSearchIndexSettingsEvent = createEvent<Pick<PortalSettings, 'searchIndexEnabled'>>();
export const updateAccessSettingsEvent = createEvent<Pick<PortalSettings, 'access'>>();
export const updateClientSettingsEvent = createEvent<Pick<PortalSettings, 'clientSettings'>>();
export const updateCustomCodeSettingsEvent =
  createEvent<Pick<PortalSettings, 'customCode' | 'customStyle' | 'customScript'>>();
export const updateMetaSettingsEvent = createEvent<Pick<PortalSettings, 'name' | 'description'>>();
export const updateRequestAccessPageEvent = createEvent<Pick<PortalSettings, 'requestAccessPage'>>();
export const updateDisableBrandingEvent = createEvent<Pick<PortalSettings, 'disableBranding'>>();
export const updateHeroSettingEvent =
  createEvent<Pick<PortalSettings, 'searchEnabled' | 'greetingEnabled' | 'greetingMessage' | 'headerBlocksEnabled'>>();
export const updateAIAssistantSettingsEvent = createEvent<Pick<PortalSettings, 'aiAssistantSettings'>>();

export const $settings = createStore(initialSettingsState)
  .on(effectorStartEvent, (state) => state)
  .on(updateSearchAnalyticsSettingsEvent, (state, { searchAnalyticsEnabled }) => ({
    ...state,
    searchAnalyticsEnabled,
  }))
  .on(updatePortalAnalyticsSettingsEvent, (state, { portalAnalyticsEnabled }) => ({
    ...state,
    portalAnalyticsEnabled,
  }))
  .on(updateSearchIndexSettingsEvent, (state, { searchIndexEnabled }) => ({
    ...state,
    searchIndexEnabled,
  }))
  .on(updateAccessSettingsEvent, (state, { access }) => {
    return {
      ...state,
      access,
    };
  })
  .on(updateClientSettingsEvent, (state, value) => {
    return {
      ...state,
      clientSettings: value.clientSettings ? value.clientSettings : initialClientSettings,
    };
  })
  .on(updateCustomCodeSettingsEvent, (state, { customCode, customStyle, customScript }) => {
    return {
      ...state,
      customCode: customCode ?? '',
      customScript: customScript ?? '',
      customStyle: customStyle ?? '',
    };
  })
  .on(updateMetaSettingsEvent, (state, { name, description }) => ({
    ...state,
    name,
    description,
  }))
  .on(updateRequestAccessPageEvent, (state, { requestAccessPage }) => ({
    ...state,
    requestAccessPage: {
      ...state.requestAccessPage,
      ...requestAccessPage,
    },
  }))
  .on(updateDisableBrandingEvent, (state, { disableBranding }) => ({
    ...state,
    disableBranding,
  }))
  .on(updateHeroSettingEvent, (state, hero) => ({
    ...state,
    ...hero,
  }))
  .on(updateAIAssistantSettingsEvent, (state, aiAssistantSettings) => ({
    ...state,
    ...aiAssistantSettings,
  }));

export const $portalName = $settings.map((state) => state.name);
export const $portalDesc = $settings.map((state) => state.description);
export const $requestAccessPage = $settings.map((state) => state.requestAccessPage);
export const $searchAnalyticsEnabled = $settings.map((state) => state.searchAnalyticsEnabled);
export const $portalAnalyticsEnabled = $settings.map((state) => state.portalAnalyticsEnabled);
export const $searchIndexEnabled = $settings.map((state) => state.searchIndexEnabled);
export const $language = $settings.map((state) => state.language);
export const $portalDomain = $settings.map((state) => state.domain);
export const $searchEnabled = $settings.map((state) => state.searchEnabled);
export const $greetingEnabled = $settings.map((state) => state.greetingEnabled);
export const $greetingMessage = $settings.map((state) => state.greetingMessage);
export const $headerBlocksEnabled = $settings.map((state) => state.headerBlocksEnabled);
export const $accessSettings = $settings.map((state) => state.access);
export const $portalAIAssistantSettings = $settings.map((state) => state.aiAssistantSettings);
export const $clientSettings = $settings.map((state) => state.clientSettings);
export const $accessConsole = $accessSettings.map((state) => state.console);
export const $accessPortal = $accessSettings.map((state) => state.portal);
export const $customCode = $settings.map((state) => state.customCode);
export const $customScript = $settings.map((state) => state.customScript);
export const $customStyle = $settings.map((state) => state.customStyle);
export const $disableBranding = $settings.map(({ disableBranding }) => disableBranding);
